import { useEffect, useRef, useState } from 'react'

/* ---------- Palmer notation layout ----------
   Upper:  UR 8 7 6 5 4 3 2 1 | 1 2 3 4 5 6 7 8 UL
   Lower:  LR 8 7 6 5 4 3 2 1 | 1 2 3 4 5 6 7 8 LL
   Code = quadrant + number, e.g. "UR6", "LL1"
------------------------------------------------ */
export const DEFAULT_FINDINGS = [
  { key: 'CARIES',     label: 'Caries',       color: '#d64545' },
  { key: 'MISSING',    label: 'Missing',      color: '#8a8a8a' },
  { key: 'FILLED',     label: 'Restored',     color: '#2f7fd6' },
  { key: 'RCT',        label: 'RCT',          color: '#8f49c9' },
  { key: 'CROWN',      label: 'Crown',        color: '#d9a300' },
  { key: 'MOBILE',     label: 'Mobile',       color: '#e07b1f' },
  { key: 'IMPACTED',   label: 'Impacted',     color: '#1f9d7a' },
  { key: 'FRACTURED',  label: 'Fractured',    color: '#b0413e' },
  { key: 'EDENTULOUS', label: 'Edentulous',   color: '#3b3b3b' }
]
let FINDINGS = DEFAULT_FINDINGS
const findingColor = (k) => FINDINGS.find((f) => f.key === k)?.color || '#999'
const findingLabel = (k) => FINDINGS.find((f) => f.key === k)?.label || k

const RIGHT = [8, 7, 6, 5, 4, 3, 2, 1]
const LEFT = [1, 2, 3, 4, 5, 6, 7, 8]

/* Crown + root outlines, drawn with roots pointing UP (upper arch); lower arch is flipped. */
function toothShape(n) {
  if (n >= 6) // molars: 2 roots, broad crown
    return 'M3 20 L4 6 Q4 2 8 2 L9 2 Q11 2 11.5 6 L12 14 L12.5 6 Q13 2 15 2 L16 2 Q20 2 20 6 L21 20 Q22 32 12 32 Q2 32 3 20 Z'
  if (n >= 4) // premolars: single root, rounded crown
    return 'M7 20 L8 6 Q8 2 12 2 Q16 2 16 6 L17 20 Q18 32 12 32 Q6 32 7 20 Z'
  if (n === 3) // canine: long root, pointed cusp
    return 'M8 20 L10 4 Q12 0 14 4 L16 20 Q18 30 12 33 Q6 30 8 20 Z'
  // incisors: single root, flat edge
  return 'M8 20 L9 5 Q10 2 12 2 Q14 2 15 5 L16 20 L16 31 L8 31 Z'
}

function Tooth({ code, n, lower, finding, onClick }) {
  const color = finding ? findingColor(finding.findingCode) : '#fff'
  const missing = finding?.findingCode === 'MISSING' || finding?.findingCode === 'EDENTULOUS'
  return (
    <div className={`tooth ${finding ? 'marked' : ''}`} onClick={onClick} title={`${code}${finding ? ' – ' + finding.findingCode : ''}${finding?.note ? ' (' + finding.note + ')' : ''}`}>
      {!lower && <span className="tnum">{n}</span>}
      <svg viewBox="0 0 24 34" className="tsvg" style={lower ? { transform: 'scaleY(-1)' } : undefined}>
        <path d={toothShape(n)} fill={missing ? 'none' : color} stroke={missing ? '#999' : '#444'}
              strokeWidth="1.2" strokeDasharray={missing ? '2 2' : undefined} opacity={missing ? 0.6 : 1} />
        {finding?.findingCode === 'RCT' && <line x1="12" y1="4" x2="12" y2="22" stroke="#fff" strokeWidth="2" />}
        {finding?.findingCode === 'FRACTURED' && <path d="M6 24 L11 27 L8 30 L14 33" stroke="#fff" strokeWidth="1.5" fill="none" />}
        {finding?.findingCode === 'MISSING' && <path d="M5 8 L19 30 M19 8 L5 30" stroke="#999" strokeWidth="1.3" />}
      </svg>
      {lower && <span className="tnum">{n}</span>}
    </div>
  )
}

function Picker({ code, current, onSelect, onClear, onClose, anchorRef }) {
  const [note, setNote] = useState(current?.note || '')
  const ref = useRef(null)
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose() }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [onClose])
  return (
    <div className="tf-pop picker" ref={ref}>
      <div className="tf-pop-title">Tooth {code} <button type="button" onClick={onClose}>✕</button></div>
      <div className="chips">
        {FINDINGS.map((f) => (
          <button type="button" key={f.key}
                  className={`chip ${current?.findingCode === f.key ? 'on' : ''}`}
                  style={{ '--c': f.color }}
                  onClick={() => onSelect(f.key, note)}>{f.label}</button>
        ))}
      </div>
      <input placeholder="Note (e.g. MO caries, Grade II)" value={note} onChange={(e) => setNote(e.target.value)}
             onKeyDown={(e) => { if (e.key === 'Enter' && current) onSelect(current.findingCode, note) }} />
      <div className="row-end">
        {current && <button type="button" onClick={onClear}>Clear</button>}
        {current && <button type="button" className="primary" onClick={() => onSelect(current.findingCode, note)}>Save note</button>}
      </div>
    </div>
  )
}

/**
 * Props: findings: [{palmerCode, finding, note}], onChange(findings)
 */
export default function ToothChart({ findings, onChange, master }) {
  if (master && master.length) FINDINGS = master.map((m) => ({ key: m.findingCode, label: m.findingName, color: m.colorHex }))
  const [sel, setSel] = useState(null)
  const byCode = Object.fromEntries(findings.map((f) => [f.palmerCode, f]))

  const setFinding = (code, finding, note) => {
    const rest = findings.filter((f) => f.palmerCode !== code)
    onChange([...rest, { palmerCode: code, findingCode: finding, note }])
    setSel(null)
  }
  const clear = (code) => { onChange(findings.filter((f) => f.palmerCode !== code)); setSel(null) }

  const row = (quadR, quadL, lower) => (
    <div className={`arch ${lower ? 'lower' : 'upper'}`}>
      <span className="qlabel">{quadR}</span>
      <div className="quad">
        {RIGHT.map((n) => { const c = quadR + n; return <Tooth key={c} code={c} n={n} lower={lower} finding={byCode[c]} onClick={() => setSel(c)} /> })}
      </div>
      <div className="midline" />
      <div className="quad">
        {LEFT.map((n) => { const c = quadL + n; return <Tooth key={c} code={c} n={n} lower={lower} finding={byCode[c]} onClick={() => setSel(c)} /> })}
      </div>
      <span className="qlabel">{quadL}</span>
    </div>
  )

  return (
    <div className="chart">
      {row('UR', 'UL', false)}
      <div className="hline" />
      {row('LR', 'LL', true)}
      <div className="legend">
        {FINDINGS.map((f) => <span key={f.key}><i style={{ background: f.color }} />{f.label}</span>)}
      </div>
      {findings.length > 0 && (
        <div className="marks">
          {findings.sort((a, b) => a.palmerCode.localeCompare(b.palmerCode)).map((f) => (
            <span key={f.palmerCode} className="mark" style={{ '--c': findingColor(f.finding) }} onClick={() => setSel(f.palmerCode)}>
              {f.palmerCode} {findingLabel(f.findingCode)}{f.note ? ` – ${f.note}` : ''}
            </span>
          ))}
        </div>
      )}
      {sel && <Picker code={sel} current={byCode[sel]} onSelect={(k, note) => setFinding(sel, k, note)}
                      onClear={() => clear(sel)} onClose={() => setSel(null)} />}
    </div>
  )
}
